import interpret from "format-message-interpret";
import parse from "format-message-parse";
import { Guid } from "guid-typescript";
import { LocalDate, LocalTime } from "js-joda";

import { validationMessages } from "infrastructure/localization";

import { displayMessages } from "../../translation-managers.localization";

import { IToken, IValidationResult, ITokenDescriptionValidationResult } from "./MessageId.state";

export enum TokenType {
  id = "id",
  type = "type",
  style = "style",
  selector = "selector",
  syntax = "syntax"
}

export enum TokenFormat {
  variable = "variable",
  select = "select",
  plural = "plural",
  selectOrdinal = "selectordinal",
  date = "date",
  number = "number",
  time = "time"
}

const validateMessageIdFormat = (messageId: string): boolean => {
  const messageRegex = new RegExp("^([a-z]+[a-zA-Z]*([A-Z])?-){3,4}([a-z0-9]+[a-zA-Z0-9]*[A-Z]?)$");
  return messageRegex.test(messageId);
};

export const getTokenParameters = (textElements: parse.Element[]): IToken[] => {
  if (!textElements) {
    return [];
  }

  let stack = textElements.slice();
  const parameters: IToken[] = [];
  const usedTokens = new Set();

  while (stack.length) {
    const element = stack.pop();
    if (element && element.length === 1 && element[0] === "#") {
      continue;
    }

    if (element && typeof element !== "string") {
      const name = element[0];
      const type = element[1];
      if (!usedTokens.has(name)) {
        parameters.push({ id: Guid.raw(), name: name, type: type ? type : "", originalType: type ? type : "", description: "" });
      }

      usedTokens.add(name);

      const childElements = element[3] || element[2];
      if (childElements && typeof childElements === "object") {
        const children = Object.keys(childElements).map((key: string) => childElements && childElements[key]);
        stack = stack.concat(...children);
      }
    }
  }

  return parameters.reverse();
};

export const getTokenValues = (tokens: IToken[]) => {
  const tokenValues = {};
  tokens.forEach((token: IToken) => {
    if (!token.customToken) {
      if (token.type === TokenFormat.date) {
        tokenValues[token.name] = !token.value ? LocalDate.now() : token.value;
      } else if (token.type === TokenFormat.time) {
        tokenValues[token.name] = !token.value ? LocalTime.now() : token.value;
      } else {
        tokenValues[token.name] = !token.value ? `{${token.name}}` : token.value;
      }
    }
  });

  return tokenValues;
};

export const validateMessageId = (messageId: string): IValidationResult => {
  const maxLength = 150;
  if (!messageId || messageId === " ") {
    return {
      success: false,
      errorMessageId: validationMessages.error("propertyIsRequired"),
      tokens: { property: displayMessages.property("messageId") }
    };
  }

  if (messageId.length > maxLength) {
    return {
      success: false,
      errorMessageId: validationMessages.error("propertyExceededMaxLength"),
      tokens: {
        property: displayMessages.property("messageId"),
        maxLength: maxLength,
        actualLength: messageId.length
      }
    };
  }

  const result: boolean = validateMessageIdFormat(messageId);
  if (!result) {
    return {
      success: false,
      errorMessageId: displayMessages.warn("invalidMessageFormat")
    };
  }

  return { success: true };
};

export const validateTextField = (text: string): IValidationResult => {
  if (!text || text === " ") {
    return {
      success: false,
      errorMessageId: validationMessages.error("propertyIsRequired"),
      tokens: { property: displayMessages.property("text") }
    };
  }

  try {
    parse(text);
  } catch (error) {
    return {
      success: false,
      errorMessageId: validationMessages.error("invalidTextFormat")
    };
  }

  return { success: true };
};

export const validateMessageTextWithTokens = (text: string, tokens: IToken[]): IValidationResult => {
  const textResult: IValidationResult = validateTextField(text);
  if (!textResult.success) {
    return textResult;
  }

  const tokenValues = getTokenValues(tokens);

  try {
    const textElements: parse.Element[] = parse(text);
    interpret(textElements)(tokenValues);
  } catch (error) {
    return {
      success: false,
      errorMessageId: validationMessages.error("invalidTextFormat")
    };
  }

  return { success: true };
};

export const validateTokenDescriptions = (tokens: IToken[]): [boolean, ITokenDescriptionValidationResult] => {
  const tokenNames = {};

  const validationErrors: ITokenDescriptionValidationResult = {};

  tokens.forEach(token => {
    const descriptionRequired = !token.description || token.description === " ";
    const tokenNameRequired = !token.name || token.name === " ";
    const duplicateTokenName = tokenNames[token.name] ? true : false;
    const invalidTokenType = !token.customToken && token.type !== token.originalType;

    validationErrors[token.id] = {
      tokenId: token.id,
      descriptionRequired,
      tokenNameRequired,
      duplicateTokenName,
      invalidTokenType,
      isValid: !descriptionRequired && !tokenNameRequired && !duplicateTokenName && !invalidTokenType
    };

    if (!tokenNames[token.name]) {
      tokenNames[token.name] = token.name;
    }
  });

  const notValid = Object.values(validationErrors).some(result => result.isValid === false);
  return [!notValid, validationErrors];
};
